<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="workOrdersFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            :height="$vuetify.breakpoint.mdAndDown ? '64vh' : '64vh'"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Work Orders Per Page',
            }"
            @click:row="openProcessWorkOrder"
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                   <v-col cols="12" class="mb-n2">
                        <h1 class="mb-0">WORK ORDERS</h1>
                        <div class="d-flex flex-wrap justify-start mt-0 pl-0">
                            <v-card v-for="(insight, idx) in cardInsights" :key="insight.title"
                                :class="['pt-3 pb-2 pr-2 mt-2 mb-2 mr-2 cursor-pointer', idx === 0 ? 'ml-0' : 'ml-2']"
                                min-width="200px" max-width="200px"
                                :outlined="shipmentFilter !== (idx === 0 ? 'domestic' : 'exportations')"
                                :elevation="shipmentFilter === (idx === 0 ? 'domestic' : 'exportations') ? 4 : 0"
                                @click="setShipmentFilter(idx === 0 ? 'domestic' : 'exportations')">
                                <v-card-text class="d-flex my-0 py-0 mx-0 px-0" width="100%">
                                    <v-chip class="mb-1 mt-0 mx-2 pt-0 white--text" :color="insight.color" label
                                        :style="{ height: '50px' }">
                                        <v-icon center>{{ insight.icon }}</v-icon>
                                    </v-chip>
                                    <div class="mb-1 mt-0 pt-0">
                                        <span class="d-flex mr-0 pr-0 text-capitalize"
                                            :style="{ 'font-size': '17px', 'font-weight': 'bold' }">
                                            {{ insight.title }}
                                        </span>
                                        <p class="text-h6 black--text ma-0">
                                            {{ idx === 0 ? domesticCount : exportationsCount }}
                                        </p>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.releaseDate`]="{ header }">
                <div @click.stop style="display: flex; align-items: center;">
                    <v-text-field
                        :label="header.text"
                        v-model="releaseDateToFilter"
                        dense
                        hide-details
                        style="margin: 0; padding: 0; max-width: 150px;"
                        @click.stop
                    />
                </div>
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.projectManager`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.projectManager.name }}</p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.project?.reference }} - {{ item.project?.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.weight`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">{{ (item.weight || 0).toFixed(1) }} kg</p>
                </div>
            </template>
            <template v-slot:[`item.packableWeight`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">
                        {{ (item.packableWeight || 0).toFixed(1) }} kg
                    </p>
                </div>
            </template>
           <template v-slot:[`item.lastProductionDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.lastProductionDateLabel || '-' }}</p>
                </div>
            </template>
            <template v-slot:[`item.releaseDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.releaseDateLabel || '-' }}</p>
                </div>
            </template>
        </v-data-table>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--PROCESS WORK ORDER-->
        <v-dialog
            v-model="processWorkOrder"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProcessWorkOrder
                v-if="processWorkOrder"
                :order="selectedOrder"
                @close="closeProcessWorkOrder"
            />
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { firestore } from '@/services/firebase'
import API from '@/services/api'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import WOMethods from '@/components/WorkOrders/WOMethods.js'

export default {
    name: 'ReadyForPackingWO',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        project: {
            type: Object,
            deafult: () => ({}),
        },
    },
    components: {
        ProcessWorkOrder: () =>
            import('@/components/WorkOrders/ProcessWorkOrder.vue'),
    },
    data: () => ({
        selectedOrder: undefined,
        processWorkOrder: false,
        workOrder: false,
        projectToFilter: undefined,
        projectToFilterDebounced: undefined,
        releaseDateToFilter: undefined,
        releaseDateToFilterDebounced: undefined,
        loading: false,
        workOrders: [],
        activateAlert: false,
        alertMessage: undefined,
        search: null,
        searchDebounced: null,
        shipmentFilter: null,
        cardInsights: [
            { color: '#20844c', icon: 'mdi-home', title: 'Domestic' },
            { color: '#3498DB', icon: 'mdi-airplane', title: 'Exportations' },
        ],
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'PROJECT MANAGER',
                value: 'projectManager',
                align: 'center',
                sortable: false,
                width: 200,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
                width: 240,
            },
            {
                text: 'TOTAL MASS',
                value: 'weight',
                align: 'right',
                sortable: false,
                width: 115,
            },
            {
                text: 'PACKABLE MASS',
                value: 'packableWeight',
                align: 'right',
                sortable: false,
                width: 115,
            },
            {
                text: 'READY FOR PACKING',
                value: 'qtyReadyForPacking',
                align: 'center',
                sortable: false,
                width: 115,
            },
            {
                text: 'RECEIVED PENDING TO RELEASE',
                value: 'qtyReceivedPendingToRelease',
                align: 'center',
                sortable: true,
                width: 150,
            },
            {
                text: 'PENDING TO RECEIVE',
                value: 'pendingtoReceive',
                align: 'center',
                sortable: true,
                width: 150,
            },
            {
                text: 'LAST PRODUCTION DATE',
                value: 'lastProductionDate',
                align: 'center',
                sortable: true,
                width: 180,
            },
            {
                text: 'LAST RELEASE DATE',
                value: 'releaseDate',
                align: 'center',
                sortable: true,
                width: 180,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'center',
                sortable: false,
            },
        ],
        company: JSON.parse(localStorage.getItem('company')),
        listener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        enableOptions: true,
        settingProcesses: undefined,
        qualityProcess: {
            id: 'quality',
            name: 'Quality',
        },
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Ready for packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        user: {},
        woMethods: new WOMethods(),
    }),
    computed: {
        ...mapState(['notificationResource']),
        workOrdersBeforeShipmentFilter() {
            let filtered = this.workOrders
            const hasFilters =
                this.searchDebounced ||
                this.projectToFilterDebounced ||
                this.releaseDateToFilterDebounced

            if (hasFilters) {
                const conditions = []
                if (this.searchDebounced) conditions.push(this.filterOrder)
                if (this.projectToFilterDebounced) conditions.push(this.filterProject)
                if (this.releaseDateToFilterDebounced) conditions.push(this.filterReleaseDate)
                filtered = filtered.filter(order =>
                    conditions.every(condition => condition(order))
                )
            }
            if (!hasFilters) {
                filtered = filtered.filter(
                    wo => +wo.packableWeight > 0 || wo.qtyReadyForPacking > 0
                )
            }
            return filtered
        },
        domesticCount() {
            return this.workOrdersBeforeShipmentFilter.filter(
                wo => (wo.woCountry || '') === 'colombia'
            ).length
        },
        exportationsCount() {
            return this.workOrdersBeforeShipmentFilter.filter(
                wo => (wo.woCountry || '') !== 'colombia'
            ).length
        },
        workOrdersFiltered() {
            let filtered = this.workOrdersBeforeShipmentFilter

            if (this.shipmentFilter === 'domestic') {
                filtered = filtered.filter(wo => (wo.woCountry || '') === 'colombia')
            } else if (this.shipmentFilter === 'exportations') {
                filtered = filtered.filter(wo => (wo.woCountry || '') !== 'colombia')
            }

            return [...filtered].sort(
                (a, b) =>
                    (b.qtyReadyForPacking || 0) - (a.qtyReadyForPacking || 0)
            )
        },
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.setNotificationResource(undefined)
            }
        },
        search: function(newValue) {
            this.debouncedSearch(newValue)
        },
        projectToFilter: function(newValue) {
            this.debouncedProjectFilter(newValue)
        },
        releaseDateToFilter: function(newValue) {
            this.debouncedReleaseDateFilter(newValue)
        },
    },
    created() {
        this.workOrdersById = new Map()
        this.debouncedSearch = _.debounce(value => {
            this.searchDebounced = value
        }, 400)
        this.debouncedProjectFilter = _.debounce(value => {
            this.projectToFilterDebounced = value
        }, 400)
        this.debouncedReleaseDateFilter = _.debounce(value => {
            this.releaseDateToFilterDebounced = value
        }, 400)
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            const {
                data: { settings },
            } = await API.getSettings()
            this.settingProcesses = settings.find(s => s.name == 'Processes')
            this.projects = await API.getWoProjects()
            const {
                data: { users },
            } = await API.getWoUsers()
            this.users = users
            await this.getWOFromSDK()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.listener && typeof this.listener === 'function') {
            this.listener()
        }
        this.listener = null
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                ['approved', 'finished']
            )
        },
        async getWOFromSDK() {
            try {
                this.loading = true
                let startDate = new Date()
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .where('status', 'in', [
                        'rejected',
                        'approved',
                        'finished',
                        'closed',
                        'deleted',
                    ])
                    .where('updatedOn', '>=', startDate)
                if (!this.user.permissions.includes('readAllWO')) {
                    query = query.where('createdBy', '==', this.user.id)
                }

                // Load initial data from backup
                let initialData = await this.loadAllActiveWorkOrders()

                //filter by status
                initialData = initialData.filter(wo =>
                    ['approved', 'finished'].includes(wo.status)
                )
                if (!this.user.permissions.includes('readAllWO')) {
                    initialData = initialData.filter(
                        wo => wo.createdBy == this.user.id
                    )
                }

                // Initialize workOrders as reactive array
                this.workOrders = []

                for (let wo of initialData) {
                    wo = await this.formatWorkOrder(wo)
                    if (wo) {
                        this.setWorkOrderInMemory(wo)
                    }
                }

                this.listener = query.onSnapshot(async snapshot => {
                    const changes = Array.from(snapshot.docChanges())
                    const processedChanges = await Promise.all(
                        changes.map(async change => {
                            let wo = Object.assign(change.doc.data(), {
                                id: change.doc.id,
                            })
                            wo = await this.formatWorkOrder(wo)
                            return { change, wo }
                        })
                    )

                    // Process all changes after all have been formatted
                    processedChanges.forEach(({ change, wo }) => {
                        if (wo) {
                            if (
                                change.type === 'added' ||
                                change.type === 'modified'
                            ) {
                                this.setWorkOrderInMemory(wo)
                            }
                            if (
                                change.type === 'removed' ||
                                (wo.status !== 'approved' &&
                                    wo.status !== 'finished')
                            ) {
                                this.removeWorkOrderFromMemory(wo.id)
                            }
                        }
                    })

                    // update selectedOrder if it's currently open
                    if (this.selectedOrder && this.selectedOrder.id) {
                        const updatedOrder = this.workOrdersById.get(
                            this.selectedOrder.id
                        )
                        if (updatedOrder) {
                            this.selectedOrder = _.cloneDeep(updatedOrder)
                        }
                    }
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async formatWorkOrder(wo) {
            try {
                const workOrder = wo

                if (
                    ![
                        'rejected',
                        'approved',
                        'finished',
                        'deleted',
                        'closed',
                    ].includes(workOrder.status)
                ) {
                    return undefined
                }

                // Filter by readAllWO permission in code (not in query)
                if (
                    !this.user?.permissions?.includes('readAllWO') &&
                    workOrder.createdBy != this.user.id
                ) {
                    return undefined
                }

                const user = this.users[workOrder.projectManager]
                if (user) {
                    workOrder.projectManager = user
                } else {
                    workOrder.projectManager = {
                        id: workOrder.projectManager,
                    }
                    workOrder.projectManager.name = ''
                }

                workOrder.project = this.projects[workOrder.projectId]
                if (!workOrder.project) {
                    workOrder.project = await API.getProject(
                        workOrder.projectId
                    )
                    this.projects[workOrder.projectId] = workOrder.project
                }

                // Format items
                const formattedWO = this.setProcessesInfo(
                    this.woMethods.formatItems(workOrder)
                )
                return formattedWO
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
                return undefined
            }
        },
        buildWorkOrderRow(wo) {
            return {
                id: wo.id,
                code: wo.code,
                projectManager: wo.projectManager,
                project: wo.project,
                weight: wo.weight,
                packableWeight: wo.packableWeight,
                qtyReadyForPacking: wo.qtyReadyForPacking,
                qtyReceivedPendingToRelease: wo.qtyReceivedPendingToRelease,
                pendingtoReceive: wo.pendingtoReceive,
                releaseDate: wo.releaseDate,
                releaseDateLabel: wo.releaseDate
                    ? this.formatDate(wo.releaseDate)
                    : null,
                lastProductionDate: wo.lastProductionDate,
                lastProductionDateLabel: wo.lastProductionDateLabel,
                notes: wo.notes,
                _codeKey: (wo.code || '').toString().toLowerCase(),
                _projectKey: `${wo.project?.name || ''} ${wo.project
                    ?.reference || ''}`
                    .toLowerCase()
                    .trim(),
                woCountry: (wo.project?.country || '').toLowerCase().trim(),
            }
        },
        setWorkOrderInMemory(wo) {
            this.workOrdersById.set(wo.id, wo)
            const row = this.buildWorkOrderRow(wo)
            const index = this.workOrders.findIndex(w => w.id == wo.id)
            if (index >= 0) {
                this.$set(this.workOrders, index, row)
            } else {
                this.workOrders.push(row)
            }
        },
        removeWorkOrderFromMemory(woId) {
            this.workOrdersById.delete(woId)
            const index = this.workOrders.findIndex(w => w.id == woId)
            if (index >= 0) {
                this.$delete(this.workOrders, index)
            }
        },
        openProcessWorkOrder(item) {
            const fullWorkOrder = this.workOrdersById.get(item.id)
            if (!fullWorkOrder) {
                return
            }
            this.selectedOrder = _.cloneDeep(fullWorkOrder)
            this.processWorkOrder = true
        },
        closeProcessWorkOrder() {
            this.processWorkOrder = false
        },
        setShipmentFilter(value) {
            this.shipmentFilter =
                this.shipmentFilter === value ? null : value
        },
        filterOrder(order) {
            return (order._codeKey || '').includes(
                this.searchDebounced.toLowerCase()
            )
        },
        filterProject(order) {
            return (order._projectKey || '').includes(
                this.projectToFilterDebounced.toLowerCase()
            )
        },
        filterReleaseDate(order) {
            return (
                order.releaseDateLabel &&
                order.releaseDateLabel
                    .toLowerCase()
                    .includes(this.releaseDateToFilterDebounced.toLowerCase())
            )
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('L')} ${moment
                .unix(seconds)
                .format('LT')}`
        },
        setProcessesInfo(wo) {
            let totalPacked = 0
            let totalReceived = 0
            let totalReleased = 0
            let totalPackableWeight = 0
            let sentToPackingNotReceived = 0
            let producedMinusSentPrevious = 0

            let releaseSeconds = null
            let dateSet = false
            let lastProductionDateSeconds = null
            wo.items.forEach(item => {
                if (item.processes) {
                    if (item.processes.includes(this.packingProcess.id)) {
                        totalPackableWeight += item.quantity * (item.mass || 0)
                    }

                    if (item.deliveries) {
                        let sentToPacking = item.deliveries.filter(
                            register =>
                                register.nextProcess.id ==
                                    this.packingProcess.id ||
                                register.nextProcess.id ==
                                    this.readyForPackingProcess.id
                        )
                        const sentToPackingQty = sentToPacking.reduce(
                            (acc, d) => acc + (d.qty || 0),
                            0
                        )

                        sentToPackingNotReceived += sentToPacking.reduce(
                            (acc, d) => acc + (d.receivedBy ? 0 : (d.qty || 0)),
                            0
                        )

                        if (sentToPacking.length > 0) {
                            releaseSeconds = Date.now() / 1000
                        }

                        sentToPacking.forEach(d => {
                            if (
                                d.deliveredOn &&
                                (d.deliveredOn.seconds ||
                                    d.deliveredOn._seconds) < releaseSeconds
                            ) {
                                dateSet = true
                                releaseSeconds =
                                    d.deliveredOn.seconds ||
                                    d.deliveredOn._seconds
                            }
                        })

                        item.received = sentToPacking.reduce(
                            (acc, d) => acc + (d.receivedBy ? d.qty || 0 : 0),
                            0
                        )
                        totalReceived += item.received

                        item.released = (item.quality || []).reduce(
                            (accum, q) => accum + Number(q.releaseQty || 0),
                            0
                        )
                        if (item.processes.includes(this.qualityProcess.id)) {
                            if (item.quality) {
                                totalReleased += item.quality.reduce(
                                    (total, q) => total + (q.releaseQty || 0),
                                    0
                                )
                            }
                        } else {
                            totalReleased += sentToPacking.reduce(
                                (total, register) => total + register.qty,
                                0
                            )
                        }

                        // Pending to receive: produced minus sent in the process before packing
                        if (item.processes.includes(this.packingProcess.id) || item.processes.includes(this.readyForPackingProcess.id)) {
                            let producedInPrev = 0
                            const sentFromPrev = sentToPackingQty
                            const itemProducedQty = (item.production || []).reduce(
                                (total, entry) => total + (entry.qty || 0),
                                0
                            )
                            if (item.processes.includes(this.qualityProcess.id)) {
                                producedInPrev = item.released
                            } else {
                                const processBeforePacking = sentToPacking.find(
                                    delivery => delivery.prevProcess
                                )?.prevProcess
                                if (processBeforePacking?.id != null) {
                                    const receivedByProcessBeforePacking = (item.deliveries || []).filter(
                                        delivery => delivery.nextProcess?.id === processBeforePacking.id && delivery.receivedBy
                                    ).reduce((total, delivery) => total + (delivery.qty || 0), 0)
                                    if (receivedByProcessBeforePacking > 0) {
                                        producedInPrev = receivedByProcessBeforePacking
                                    } else {
                                        const productionInProcessBeforePacking = (item.production || []).filter(
                                            entry => entry.process?.id === processBeforePacking.id
                                        ).reduce((total, entry) => total + (entry.qty || 0), 0)
                                        producedInPrev = productionInProcessBeforePacking > 0 ? productionInProcessBeforePacking : itemProducedQty
                                    }
                                } else {
                                    producedInPrev = itemProducedQty
                                }
                                if (producedInPrev === 0) producedInPrev = item.quantity
                            }
                            producedMinusSentPrevious += Math.max(0, producedInPrev - sentFromPrev)
                        }
                    }
                    // Last production date: if item has packing use process-before-packing; otherwise use most recent from any process
                    if (item.production?.length) {
                        const hasPacking = item.processes?.includes(this.packingProcess.id) || item.processes?.includes(this.readyForPackingProcess.id)
                        let processBeforePackingId = null
                        if (hasPacking) {
                            // Resolve process before packing from deliveries (skip quality, use the one that feeds quality)
                            if (item.deliveries?.length) {
                                const sentToPackingForDate = item.deliveries.filter(
                                    d => d.nextProcess?.id === this.packingProcess.id || d.nextProcess?.id === this.readyForPackingProcess.id
                                )
                                processBeforePackingId = sentToPackingForDate.find(d => d.prevProcess)?.prevProcess?.id
                                if (processBeforePackingId === this.qualityProcess.id) {
                                    const deliveryToQuality = item.deliveries.find(d => d.nextProcess?.id === this.qualityProcess.id)
                                    processBeforePackingId = deliveryToQuality?.prevProcess?.id
                                }
                            }
                            // Fallback: use process order from settings when there are no deliveries to packing
                            if (!processBeforePackingId && this.settingProcesses?.processes?.length && item.processes?.length) {
                                const orderedIds = this.settingProcesses.processes
                                    .filter(p => item.processes.includes(p.id))
                                    .map(p => p.id)
                                const packingIdx = orderedIds.findIndex(
                                    id => id === this.packingProcess.id || id === this.readyForPackingProcess.id
                                )
                                if (packingIdx > 0) {
                                    processBeforePackingId = orderedIds[packingIdx - 1]
                                    if (processBeforePackingId === this.qualityProcess.id && packingIdx > 1) {
                                        processBeforePackingId = orderedIds[packingIdx - 2]
                                    }
                                }
                            }
                        }
                        // Restrict to that process if we have it; otherwise use all production entries with producedOn
                        const entriesToConsider = processBeforePackingId
                            ? item.production.filter(entry => entry.process?.id === processBeforePackingId && entry.producedOn)
                            : item.production.filter(entry => entry.producedOn)
                        entriesToConsider.forEach(entry => {
                            const seconds = entry.producedOn?.seconds ?? entry.producedOn?._seconds
                            if (seconds != null && (lastProductionDateSeconds == null || seconds > lastProductionDateSeconds)) {
                                lastProductionDateSeconds = seconds
                            }
                        })
                    }
                }
                totalPacked += Number(item.packedQty) || 0
            })
            wo.lastProductionDate = lastProductionDateSeconds
            wo.lastProductionDateLabel = lastProductionDateSeconds
                ? this.formatDate(lastProductionDateSeconds)
                : null
            wo.qtyReadyForPacking =
                Math.min(totalReceived, totalReleased) - totalPacked

            wo.qtyReceivedPendingToRelease = this.getTotalReceivedPendingToRelease(
                wo
            )
            wo.pendingtoReceive = sentToPackingNotReceived + producedMinusSentPrevious
            wo.releaseDate = dateSet ? releaseSeconds : null
            wo.packableWeight = totalPackableWeight
            return wo
        },
        getTotalReceivedPendingToRelease(wo) {
            //received - released
            if (wo.items) {
                return wo.items.reduce(
                    (accum, item) =>
                        accum +
                        (+item.received - item.released > 0
                            ? +item.received - item.released
                            : 0),
                    0
                )
            } else {
                return 0
            }
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}
thead {
    position: sticky;
    z-index: 1;
}
</style>
